// Lot 594 — écran maintenu allumé pendant un direct (hôte et spectateurs) et pendant la lecture d'une vidéo avec le
// son (vidéo longue, replay, reel…). App native → plugin KeepAwake (FLAG_KEEP_SCREEN_ON / isIdleTimerDisabled) ;
// web / PC → Screen Wake Lock API (Chrome, Edge, Safari 16.4+, Firefox 126+), ré-acquis quand l'onglet redevient
// visible ; ancienne app sans le plugin → repli Wake Lock de la WebView. Meilleur effort, jamais bloquant.
import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { guardOn, subscribeGuards } from "@/lib/guards";

const holders = new Set();
let sentinel = null;
let nativeOn = false;
let nativeBroken = false;
let chain = Promise.resolve();

// Lot 671 — garde-fou admin « screen_awake » : coupé → on relâche l'écran même si des lecteurs le demandent.
const wanted = () => guardOn("screen_awake") && holders.size > 0;

async function applyNative() {
  const { KeepAwake } = await import("@capacitor-community/keep-awake");
  if (wanted() && !nativeOn) { await KeepAwake.keepAwake(); nativeOn = true; }
  else if (!wanted() && nativeOn) { await KeepAwake.allowSleep(); nativeOn = false; }
}

async function applyWeb() {
  const wl = typeof navigator !== "undefined" ? navigator.wakeLock : null;
  if (!wl) return;
  if (wanted() && !sentinel) {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    const s = await wl.request("screen");
    sentinel = s;
    s.addEventListener?.("release", () => { if (sentinel === s) sentinel = null; });
  } else if (!wanted() && sentinel) {
    const s = sentinel;
    sentinel = null;
    await s.release();
  }
}

async function apply() {
  if (Capacitor.isNativePlatform() && !nativeBroken) {
    try { await applyNative(); return; } catch { nativeBroken = true; }
  }
  await applyWeb();
}

const schedule = () => { chain = chain.then(apply).catch(() => {}); return chain; };

export const holdScreenAwake = (reason) => { holders.add(reason); return schedule(); };
export const releaseScreenAwake = (reason) => { holders.delete(reason); return schedule(); };
export const screenAwakeHolders = () => Array.from(holders);
export const isScreenAwakeSupported = () => Capacitor.isNativePlatform() || !!(typeof navigator !== "undefined" && navigator.wakeLock);

// Direct côté hôte : actif tant que la caméra / l'écran est capturé ou que le direct est en cours.
export const useScreenAwake = (active, reason = "live") => {
  useEffect(() => {
    if (!active) return undefined;
    holdScreenAwake(reason);
    return () => { releaseScreenAwake(reason); };
  }, [active, reason]);
};

// Une vidéo compte si elle joue, visible à l'écran, avec le son (ou un flux caméra / direct). Les aperçus muets du
// fil ne retiennent pas l'écran (batterie) ; un reel ou une vidéo longue avec le son, un direct, un replay : oui.
export const videoHoldsScreen = (v) => !!v && !v.paused && !v.ended && v.readyState >= 2 && (!v.muted || !!v.srcObject) && v.isConnected;

let safety = null;
const recompute = () => {
  if (typeof document === "undefined") return;
  const any = Array.from(document.querySelectorAll("video")).some(videoHoldsScreen);
  if (any) holdScreenAwake("video"); else releaseScreenAwake("video");
  // Filet : un lecteur retiré du DOM sans « pause » ne doit pas garder l'écran allumé indéfiniment.
  if (any && !safety) safety = setInterval(recompute, 15000);
  if (!any && safety) { clearInterval(safety); safety = null; }
};

let installed = false;
// Écouteurs en phase de capture (les événements média ne remontent pas) : couvre tous les lecteurs de l'app.
export function installVideoScreenAwake() {
  if (installed || typeof document === "undefined") return;
  installed = true;
  ["playing", "pause", "ended", "emptied", "volumechange", "suspend"].forEach((ev) => document.addEventListener(ev, recompute, true));
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") schedule(); recompute(); });
  subscribeGuards(schedule);
}
